import { ChevronRight, Download, Search, ChevronLeft } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { formatDate, MappingStatus, MappingStyles } from "../../utils/utils";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/AdminPortal/common/Pagination";
import DatePicker from "react-datepicker";
import { useAuth } from "../../context/AuthContext";
import "../../datepicker.css";
const ApprovedApplicationTable = ({ approvedApplications, userRole }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // State for date range picker
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [pickerMode, setPickerMode] = useState("date");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };
    const handleScroll = () => {
      setIsDatePickerOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("wheel", handleScroll, { passive: true });
    window.addEventListener("touchmove", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("wheel", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
    };
  }, []);

  const ITEMS_PER_PAGE = 5;
  const statusOptions = [
    { value: "All", label: "All Statuses" },
    { value: "new-leads", label: "New Leads" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "processing", label: "Processing" },
    { value: "submitted", label: "Submitted" },
    { value: "in-review", label: "In Review" },
  ];
  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };

  const getCustomersPath = () => {
    const role = user?.role?.toLowerCase() || userRole?.toLowerCase();
    if (["admin", "subadmin"].includes(role)) {
      return `/${role}/customers`;
    } else {
      console.warn("Unknown role:", role);
      return "/admin/customers";
    }
  };

  const finalFilteredApplicants = approvedApplications.filter((app) => {
    // Safely handle undefined or null values
    const applicantName = app?.user?.name || "";
    const status = app?.status || "";
    const applicationDate = app?.createdAt || "";

    const matchesSearch = applicantName
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) || app?.loanType.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      selectedStatus === "All" ||
      status.toLowerCase() === selectedStatus.toLowerCase();

    // Date filtering
    let matchesDate = true;
     if (pickerMode === "date") {
      matchesDate =
        (!startDate || new Date(app.date) >= new Date(startDate)) &&
        (!endDate || new Date(app.date) <= new Date(endDate));
    } else if (pickerMode === "month" && startDate) {
      const appDate = new Date(app.date);
      matchesDate =
        appDate.getMonth() === startDate.getMonth() &&
        appDate.getFullYear() === startDate.getFullYear();
    } else if (pickerMode === "year" && startDate) {
      const appDate = new Date(app.date);
      matchesDate = appDate.getFullYear() === startDate.getFullYear();
    }

    return matchesSearch && matchesStatus && matchesDate;
  });
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsStatusDropdownOpen(false);
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate, selectedStatus]);

  const handleCheckboxChange = (id) => {
    if (selectedApplicants.includes(id)) {
      setSelectedApplicants(selectedApplicants.filter((i) => i !== id));
    } else {
      setSelectedApplicants([...selectedApplicants, id]);
    }
  };

  console.log("this is a application", approvedApplications);

  const handleSelectAll = () => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    if (newSelectedAll) {
      setSelectedApplicants(finalFilteredApplicants.map((app) => app._id));
    } else {
      setSelectedApplicants([]);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(finalFilteredApplicants.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentTableData = finalFilteredApplicants.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const exportToExcelAndZip = async () => {
    // Determine which applications to export: selected or all filtered
    const applicationsToExport =
      selectedApplicants.length > 0
        ? finalFilteredApplicants.filter((app) =>
            selectedApplicants.includes(app._id)
          )
        : finalFilteredApplicants; // Export based on _id

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Prepare data for Excel sheet
    const dataForExcel = applicationsToExport.map((app) => ({
      "Application ID": app._id,
      "Applicant Name": app.user?.name || app?.username || "",
      "Loan Amount": app.principle,
      "Loan Type": app.loanType,
      Status: app.status,
      "Processing Type": app.processingMethod,
      "Loan Bank Name": app.bankName,
      "Created At": formatDate(app.createdAt),
    }));

    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(dataForExcel);

    // Append worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Approved Applications");

    // Write workbook to binary array
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Create zip and add Excel file
    const zip = new JSZip();
    zip.file("approved_applications.xlsx", excelBuffer);

    // Generate zip blob
    const zipBlob = await zip.generateAsync({ type: "blob" });

    // Trigger download
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "approved_applications.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="text-white">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Approved Applications</h3>
        <div
          onClick={() => navigate(getCustomersPath())}
          className="flex items-center text-[#FFCA20] cursor-pointer hover:underline"
        >
          Go to All Loan Applications
          <ChevronRight size={18} className="ml-1" />
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="lg:flex lg:justify-between lg:items-center">
        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-4 mb-4">
          <div className="relative flex w-full lg:w-auto">
            <Search className="absolute left-3 top-2 pr-2 w-6 h-6 text-white" />
            <input
              type="text"
              placeholder="Search by Applicant Name"
              className="bg-transparent border border-[#333333] rounded-3xl text-white w-full lg:min-w-72 pl-10 pr-3 py-2 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative bg-transparent rounded-lg border-2 border-[#333333]">
            <button
              className="flex items-center px-3 py-2 focus:outline-none gap-2"
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C8 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                  fill="#FFCA20"
                />
              </svg>
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : getPickerLabel()}
            </button>

            {isDatePickerOpen && (
              <div className="absolute z-10 mt-2 bg-[#1e1e1e] rounded-md p-3">
                {/* Picker Mode Switch */}
                <div className="flex gap-2 mb-2">
                  <button
                    className={`px-3 py-1 rounded ${
                      pickerMode === "date"
                        ? "bg-yellow-500 text-black"
                        : "bg-[#333] text-white"
                    }`}
                    onClick={() => setPickerMode("date")}
                  >
                    Date
                  </button>
                  <button
                    className={`px-3 py-1 rounded ${
                      pickerMode === "month"
                        ? "bg-yellow-500 text-black"
                        : "bg-[#333] text-white"
                    }`}
                    onClick={() => setPickerMode("month")}
                  >
                    Month
                  </button>
                  <button
                    className={`px-3 py-1 rounded ${
                      pickerMode === "year"
                        ? "bg-yellow-500 text-black"
                        : "bg-[#333] text-white"
                    }`}
                    onClick={() => setPickerMode("year")}
                  >
                    Year
                  </button>
                </div>

                {/* Calendar */}
                <DatePicker
                  selected={startDate}
                  onChange={(update) => {
                    if (pickerMode === "date") {
                      setDateRange(update);
                      if (update[0] && update[1]) setIsDatePickerOpen(false);
                    } else {
                      setDateRange([update, update]);
                      setIsDatePickerOpen(false);
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange={pickerMode === "date"}
                  showMonthYearPicker={pickerMode === "month"}
                  showYearPicker={pickerMode === "year"}
                  inline
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 mb-4 w-full lg:w-auto">
          <button
            onClick={exportToExcelAndZip}
            className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold flex items-center gap-2 w-full lg:w-auto justify-center"
          >
            Download as ZIP <Download size={16} />
          </button>
        </div>
      </div>

      {/* Table */}

      <div className="  bg-[#131313] rounded-lg border border-[#333333] w-full  overflow-x-auto scrollbar-hide">
        <table className="min-w-full">
          <thead className="border-y border-[#333333]">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                APPLICATION ID
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                APPLICANT NAME
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                LOAN AMOUNT
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                STATUS
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                PROCESSING TYPE
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                LOAN BANK NAME
              </th>
            </tr>
          </thead>
          <tbody>
            {finalFilteredApplicants.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-6 text-center text-gray-400">
                  No rows found
                </td>
              </tr>
            ) : (
              currentTableData.map((application, index) => (
                <tr key={index} className="border-b border-[#333]">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      checked={selectedApplicants.includes(application._id)}
                      onChange={() => handleCheckboxChange(application._id)}
                    />
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <div>{application._id}</div>
                    <div className="text-xs">
                      {formatDate(application.createdAt)}
                    </div>
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    {application.user?.name || application.username || "N/A"}
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <div>{application.principle}</div>
                    <div className="text-xs ">{application.loanType}</div>
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${MappingStyles(
                        application.status
                      )}
                                           `}
                    >
                      {MappingStatus(application.status)}
                    </span>
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <div className="text-xs">
                      {application.processingMethod}
                    </div>
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <div className="text-xs">
                      {application.bankName || "N/A"}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 0 && (
        <div className="flex justify-center items-center mt-4 pt-4 border-t border-[#333333]">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}
    </div>
  );
};

export default ApprovedApplicationTable;
